/**
 * Packaging OS — LocaleSync (Phase 1R-A-Fix-2 → Fix-5).
 *
 * Renders an <Outlet /> for any route mounted under `/:locale/*`. If the
 * `:locale` URL segment is one of the four supported codes, sync the
 * module-level locale state to that value (so cookie + <html lang dir>
 * + every consumer of useLocale catches up).
 *
 * Phase 1R-A-Fix-5: the effect now depends ONLY on the stable string
 * `paramCode`. The previous dependency `[found, setLocale]` re-fired on
 * every render because both values had new identities each call, which
 * caused the URL-prefix locale to clobber a user-initiated language
 * switch on the language switcher. Synchronizing once per URL change is
 * what we actually want.
 *
 * Unknown locale prefixes render NotFound so unsupported codes don't
 * silently leak into the app shell.
 */

import { useEffect } from "react";
import { Outlet, useParams } from "react-router-dom";
import { findLocale } from "../i18n/locales";
import { useLocale } from "../i18n/useLocale";
import { NotFoundPage } from "../pages/NotFoundPage";

export function LocaleSync(): JSX.Element {
  const { locale: param } = useParams<{ locale?: string }>();
  const found = param ? findLocale(param) : undefined;
  const paramCode = found ? found.code : null;
  const [, setLocale] = useLocale();

  useEffect(() => {
    if (paramCode) setLocale(paramCode);
    // setLocale identity changes every render; we deliberately depend
    // only on `paramCode` so this effect runs once per URL change, not
    // on every re-render. setLocale is stable in behavior.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramCode]);

  if (!found) {
    return <NotFoundPage />;
  }
  return <Outlet />;
}
